import React, { useState, useEffect } from 'react';
import { Save, Settings, Store, Palette, Bell, Upload, X, Calendar, AlertTriangle, Gift } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { companyService } from '../../services/companyService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, auth } from '../../services/firebase';
import CustomerRegistrationQR from './CustomerRegistrationQR';

const CompanySettings = () => {
  const { user } = useAuth();
  
  const [settings, setSettings] = useState({
    companyName: '',
    description: '',
    pointsPerReal: 1,
    welcomeBonus: 100,
    minimumRedemption: 50,
    logo: '',
    primaryColor: '#3B82F6',
    accentColor: '#10B981',
    emailNotifications: true,
    smsNotifications: false, // Apenas admin pode alterar
    promotionalEmails: true,
    pointsExpiration: {
      enabled: false,
      months: 12
    },
    referralSettings: {
      enabled: false,
      referrerPoints: 50,
      referredPoints: 25
    }
  });

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  
  // Estados para administradores
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  
  // Estados para atualização de pontos existentes
  const [updatingPoints, setUpdatingPoints] = useState(false);
  const [updateResult, setUpdateResult] = useState(null);
  
  // Estados para criação de códigos de indicação
  const [generatingCodes, setGeneratingCodes] = useState(false);
  const [codeGenerationResult, setCodeGenerationResult] = useState(null);

  // Load companies list for admin users
  useEffect(() => {
    const loadCompanies = async () => {
      if (user?.role === 'admin') {
        try {
          const companiesList = await companyService.getAll();
          setCompanies(companiesList);
          if (companiesList.length > 0 && !selectedCompanyId) {
            setSelectedCompanyId(companiesList[0].id);
          }
        } catch (error) {
          console.error('Erro ao carregar empresas:', error);
        }
      }
    };

    if (user) {
      loadCompanies();
    }
  }, [user, selectedCompanyId]);

  // Load company settings on component mount
  useEffect(() => {
    const loadCompanySettings = async () => {
      const companyId = user?.role === 'admin' ? selectedCompanyId : user?.companyId;
      
      if (companyId) {
        try {
          setInitialLoading(true);
          const company = await companyService.getById(companyId);
          
          setSettings({
            companyName: company.name || '',
            description: company.description || '',
            pointsPerReal: company.pointsPerReal !== undefined ? company.pointsPerReal : 1,
            welcomeBonus: company.welcomeBonus !== undefined ? company.welcomeBonus : 100,
            minimumRedemption: company.minimumRedemption !== undefined ? company.minimumRedemption : 50,
            logo: company.logo || '',
            primaryColor: company.primaryColor || '#3B82F6',
            accentColor: company.accentColor || '#10B981',
            emailNotifications: company.emailNotifications !== undefined ? company.emailNotifications : true,
            smsNotifications: company.smsNotifications !== undefined ? company.smsNotifications : false,
            promotionalEmails: company.promotionalEmails !== undefined ? company.promotionalEmails : true,
            pointsExpiration: {
              enabled: company.pointsExpiration?.enabled || false,
              months: company.pointsExpiration?.months || 12
            },
            referralSettings: {
              enabled: company.referralSettings?.enabled || false,
              referrerPoints: company.referralSettings?.referrerPoints !== undefined ? company.referralSettings?.referrerPoints : 50,
              referredPoints: company.referralSettings?.referredPoints !== undefined ? company.referralSettings?.referredPoints : 25
            }
          });
        } catch (error) {
          console.error('Erro ao carregar configurações:', error);
        } finally {
          setInitialLoading(false);
        }
      } else {
        setInitialLoading(false);
      }
    };

    if (user) {
      loadCompanySettings();
    }
  }, [user, selectedCompanyId]);

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione apenas arquivos de imagem.');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('O arquivo deve ter no máximo 5MB.');
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return settings.logo;

    setUploading(true);
    try {
      if (!auth.currentUser) {
        throw new Error('Usuário não está autenticado no Firebase');
      }
      
      // Create a reference to the logo file
      const logoRef = ref(storage, `company-logos/${user.companyId}/${Date.now()}-${logoFile.name}`);
      
      // Upload the file and get download URL
      await uploadBytes(logoRef, logoFile);
      const downloadURL = await getDownloadURL(logoRef);
      
      return downloadURL;
    } catch (error) {
      console.error('Erro ao fazer upload da logo:', error.message);
      
      // Provide specific error messages based on error type
      if (error.code === 'storage/unauthorized') {
        alert('Erro de autorização no Firebase Storage.\n\nPossíveis causas:\n1. Aguarde alguns minutos para as regras serem aplicadas\n2. Faça logout e login novamente\n3. Use a opção "URL da Logo" como alternativa');
      } else if (error.message.includes('não está autenticado')) {
        alert('Sessão expirada. Por favor, faça login novamente.');
      } else if (error.message.includes('CORS') ||
          error.message.includes('not found') ||
          error.message.includes('access control')) {
        alert('Firebase Storage não está habilitado para este projeto.\n\nPor favor:\n1. Vá para o Console do Firebase\n2. Habilite o Storage\n3. Ou use a opção "URL da Logo" abaixo');
      } else {
        alert('Erro ao fazer upload da logo: ' + error.message + '\n\nUse a opção "URL da Logo" como alternativa.');
      }
      
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setSettings(prev => ({ ...prev, logo: '' }));
  };

  const handleUpdateExistingPoints = async () => {
    const companyId = user?.role === 'admin' ? selectedCompanyId : user?.companyId;
    
    if (!companyId) {
      alert('Erro: empresa não identificada');
      return;
    }

    if (!settings.pointsExpiration.enabled) {
      alert('A expiração de pontos deve estar habilitada para atualizar pontos existentes.');
      return;
    }

    const confirmUpdate = window.confirm(
      'Esta operação irá aplicar as configurações de expiração a todos os pontos existentes que ainda não possuem data de expiração. Deseja continuar?'
    );

    if (!confirmUpdate) {
      return;
    }

    setUpdatingPoints(true);
    setUpdateResult(null);

    try {
      // Import the pointsService
      const { pointsService } = await import('../../services/pointsService');
      
      const result = await pointsService.updateExistingPointsWithExpiration(companyId);
      
      setUpdateResult({
        success: result.success,
        updatedCount: result.updatedCount,
        message: result.success 
          ? `${result.updatedCount} pontos foram atualizados com sucesso!`
          : `Erro ao atualizar pontos: ${result.error}`
      });

      // Clear result after 5 seconds
      setTimeout(() => setUpdateResult(null), 5000);
      
    } catch (error) {
      console.error('Erro ao atualizar pontos existentes:', error);
      setUpdateResult({
        success: false,
        updatedCount: 0,
        message: 'Erro inesperado ao atualizar pontos. Tente novamente.'
      });
      
      setTimeout(() => setUpdateResult(null), 5000);
    } finally {
      setUpdatingPoints(false);
    }
  };

  const handleGenerateReferralCodes = async () => {
    const companyId = user?.role === 'admin' ? selectedCompanyId : user?.companyId;
    
    if (!companyId) {
      alert('Erro: empresa não identificada');
      return;
    }

    if (!window.confirm('Tem certeza que deseja gerar códigos de indicação para todos os clientes que ainda não possuem? Esta ação não pode ser desfeita.')) {
      return;
    }

    setGeneratingCodes(true);
    setCodeGenerationResult(null);

    try {
      const { customerService } = await import('../../services/customerService');
      
      // Buscar todos os clientes da empresa
      const customers = await customerService.getAll();
      const companyCustomers = customers.filter(customer => customer.companyId === companyId);
      
      let updatedCount = 0;
      const errors = [];

      // Gerar códigos para clientes que não possuem
      for (const customer of companyCustomers) {
        if (!customer.referralCode) {
          try {
            await customerService.addReferralCodeToCustomer(customer.id);
            updatedCount++;
          } catch (error) {
            console.error(`Erro ao gerar código para cliente ${customer.id}:`, error);
            errors.push(`${customer.name}: ${error.message}`);
          }
        }
      }

      setCodeGenerationResult({
        success: true,
        totalCustomers: companyCustomers.length,
        updatedCount,
        alreadyHadCodes: companyCustomers.length - updatedCount - errors.length,
        errors: errors.length > 0 ? errors : null,
        message: `Processo concluído! ${updatedCount} códigos foram gerados.`
      });

      setTimeout(() => setCodeGenerationResult(null), 10000);
      
    } catch (error) {
      console.error('Erro ao gerar códigos de indicação:', error);
      setCodeGenerationResult({
        success: false,
        updatedCount: 0,
        message: 'Erro inesperado ao gerar códigos. Tente novamente.'
      });
      
      setTimeout(() => setCodeGenerationResult(null), 5000);
    } finally {
      setGeneratingCodes(false);
    }
  };

  const handleSave = async () => {
    const companyId = user?.role === 'admin' ? selectedCompanyId : user?.companyId;
    
    if (!companyId) {
      alert('Erro: empresa não identificada');
      return;
    }

    setLoading(true);
    try {
      let logoURL = settings.logo;
      
      // Upload new logo if selected
      if (logoFile) {
        try {
          logoURL = await uploadLogo();
        } catch (error) {
          // If upload fails, clear file selection so user can use URL option
          console.error('Upload failed, clearing file selection');
          setLogoFile(null);
          setLogoPreview('');
          // Keep existing logo URL if user has entered one
          logoURL = settings.logo;
          
          // Don't proceed with save if this was the only change
          if (!settings.logo) {
            return;
          }
        }
      }

      // Update company settings
      const updatedSettings = {
        name: settings.companyName,
        description: settings.description,
        pointsPerReal: settings.pointsPerReal,
        welcomeBonus: settings.welcomeBonus,
        minimumRedemption: settings.minimumRedemption,
        logo: logoURL,
        primaryColor: settings.primaryColor,
        accentColor: settings.accentColor,
        emailNotifications: settings.emailNotifications,
        promotionalEmails: settings.promotionalEmails,
        pointsExpiration: settings.pointsExpiration,
        referralSettings: settings.referralSettings
      };

      // Apenas admin pode alterar configurações de SMS
      if (user?.role === 'admin') {
        updatedSettings.smsNotifications = settings.smsNotifications;
      }

      await companyService.update(companyId, updatedSettings);
      
      // Update local state
      setSettings(prev => ({ ...prev, logo: logoURL }));
      setLogoFile(null);
      setLogoPreview('');
      
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      alert('Erro ao salvar configurações. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando configurações...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Settings className="mr-3 h-8 w-8 text-blue-600" />
          Configurações da Empresa
        </h1>
        <p className="mt-2 text-gray-600">
          Personalize as configurações do seu programa de fidelidade
        </p>
      </div>

      {/* Company Selector for Admins */}
      {user?.role === 'admin' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-medium text-blue-800 mb-2">
            Selecionar Empresa (Modo Administrador)
          </h3>
          <select
            value={selectedCompanyId || ''}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Selecione uma empresa...</option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>
                {company.name || `Empresa ${company.id}`}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="space-y-8">
        {/* Company Information */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Store className="mr-2 h-5 w-5 text-blue-600" />
            Informações da Empresa
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome da Empresa
              </label>
              <input
                type="text"
                value={settings.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <textarea
                value={settings.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Descreva sua empresa e programa de fidelidade..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Logo da Empresa
              </label>
              
              {/* Current logo display */}
              {(settings.logo || logoPreview) && (
                <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <img 
                        src={logoPreview || settings.logo} 
                        alt="Logo da empresa" 
                        className="h-16 w-16 object-contain border border-gray-200 rounded-lg bg-white"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Logo atual</p>
                        <p className="text-sm text-gray-500">
                          {logoFile ? 'Nova logo selecionada' : 'Logo atual da empresa'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Logo upload */}
              <div className="space-y-4">
                {/* Info notice */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>💡 Dica:</strong> Se o upload de arquivo não funcionar, use a opção "URL da Logo" abaixo. 
                    Para habilitar upload de arquivos, configure o Firebase Storage no console.
                  </p>
                </div>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/*"
                    onChange={handleLogoFileChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="logo-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm font-medium text-gray-900">
                      {settings.logo || logoPreview ? 'Alterar logo' : 'Enviar logo'}
                    </span>
                    <span className="text-sm text-gray-500">
                      PNG, JPG até 5MB
                    </span>
                  </label>
                </div>
                
                <div className="text-center text-sm text-gray-500">ou</div>
                
                {/* URL input as fallback */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL da Logo (alternativa)
                  </label>
                  <input
                    type="url"
                    value={settings.logo}
                    onChange={(e) => handleChange('logo', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://exemplo.com/logo.png"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Cole aqui a URL de uma imagem hospedada online (ex: Imgur, Google Drive público, etc.)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Registration QR Code */}
        <CustomerRegistrationQR />

        {/* Points Configuration */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Configurações de Pontos
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pontos por Real Gasto
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={settings.pointsPerReal}
                onChange={(e) => handleChange('pointsPerReal', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bônus de Boas-vindas
              </label>
              <input
                type="number"
                min="0"
                value={settings.welcomeBonus}
                onChange={(e) => handleChange('welcomeBonus', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0 para desabilitar"
              />
              <p className="text-xs text-gray-500 mt-1">
                Pontos dados automaticamente no cadastro. Use 0 para desabilitar.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resgate Mínimo
              </label>
              <input
                type="number"
                min="0"
                value={settings.minimumRedemption}
                onChange={(e) => handleChange('minimumRedemption', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Points Expiration Settings */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Calendar className="mr-2 h-5 w-5 text-red-600" />
            Configurações de Expiração de Pontos
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="enableExpiration"
                checked={settings.pointsExpiration?.enabled || false}
                onChange={(e) => handleChange('pointsExpiration', {
                  ...settings.pointsExpiration,
                  enabled: e.target.checked
                })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="enableExpiration" className="text-sm font-medium text-gray-700">
                Ativar expiração de pontos
              </label>
            </div>

            {settings.pointsExpiration?.enabled && (
              <div className="pl-7 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Período de Expiração (meses)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="36"
                      value={settings.pointsExpiration?.months || 12}
                      onChange={(e) => handleChange('pointsExpiration', {
                        ...settings.pointsExpiration,
                        months: parseInt(e.target.value) || 1
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Pontos expiram após este período desde a data de criação.
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Importante
                      </h3>
                      <p className="mt-1 text-sm text-yellow-700">
                        • Alterar o período de expiração afetará apenas pontos futuros<br/>
                        • Pontos existentes manterão sua data de expiração original<br/>
                        • A verificação de expiração é executada automaticamente
                      </p>
                    </div>
                  </div>
                </div>

                {/* Botão para atualizar pontos existentes */}
                <div className="border-t border-yellow-200 pt-4">
                  <h4 className="text-sm font-medium text-yellow-800 mb-2">
                    Atualizar Pontos Existentes
                  </h4>
                  <p className="text-xs text-yellow-700 mb-3">
                    Aplique as configurações de expiração aos pontos que já existem no sistema.
                  </p>
                  <button
                    type="button"
                    onClick={handleUpdateExistingPoints}
                    disabled={updatingPoints}
                    className="inline-flex items-center px-3 py-2 border border-yellow-300 shadow-sm text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updatingPoints ? (
                      <>
                        <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-yellow-600 border-t-transparent rounded-full"></div>
                        Atualizando...
                      </>
                    ) : (
                      <>
                        <Calendar className="-ml-1 mr-2 h-4 w-4" />
                        Atualizar Pontos Existentes
                      </>
                    )}
                  </button>
                  
                  {/* Resultado da atualização */}
                  {updateResult && (
                    <div className={`mt-3 p-3 rounded-md ${
                      updateResult.success 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex">
                        <div className="flex-shrink-0">
                          {updateResult.success ? (
                            <div className="h-5 w-5 text-green-400">
                              <svg viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                          ) : (
                            <div className="h-5 w-5 text-red-400">
                              <svg viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="ml-3">
                          <p className={`text-sm font-medium ${
                            updateResult.success ? 'text-green-800' : 'text-red-800'
                          }`}>
                            {updateResult.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Referral Settings */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Gift className="mr-2 h-5 w-5 text-blue-600" />
            Configurações de Indicação
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="referralEnabled"
                checked={settings.referralSettings.enabled}
                onChange={(e) => handleChange('referralSettings', {
                  ...settings.referralSettings,
                  enabled: e.target.checked
                })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="referralEnabled" className="ml-2 block text-sm text-gray-900">
                Ativar sistema de indicações
              </label>
            </div>

            {settings.referralSettings.enabled && (
              <div className="ml-6 space-y-4 border-l-2 border-gray-200 pl-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pontos para quem indica
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={settings.referralSettings.referrerPoints}
                      onChange={(e) => handleChange('referralSettings', {
                        ...settings.referralSettings,
                        referrerPoints: parseInt(e.target.value) || 0
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Pontos dados para quem fez a indicação
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pontos para quem foi indicado
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={settings.referralSettings.referredPoints}
                      onChange={(e) => handleChange('referralSettings', {
                        ...settings.referralSettings,
                        referredPoints: parseInt(e.target.value) || 0
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Pontos dados para o novo cliente indicado
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Bell className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-800">
                        <strong>Como funciona:</strong> Cada cliente recebe um código único de indicação. 
                        Quando um novo cliente usa esse código no cadastro, ambos ganham os pontos configurados.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Botão para gerar códigos para clientes existentes */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        Códigos para Clientes Existentes
                      </h4>
                      <p className="text-sm text-gray-500 mt-1">
                        Gere códigos de indicação para clientes que já estão cadastrados mas ainda não possuem código.
                      </p>
                    </div>
                    <button
                      onClick={handleGenerateReferralCodes}
                      disabled={generatingCodes}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
                    >
                      {generatingCodes ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Gerando...
                        </>
                      ) : (
                        <>
                          <Gift className="h-4 w-4" />
                          Gerar Códigos
                        </>
                      )}
                    </button>
                  </div>
                  
                  {/* Resultado da geração de códigos */}
                  {codeGenerationResult && (
                    <div className={`mt-4 p-3 rounded-md ${
                      codeGenerationResult.success 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex">
                        <div className="flex-shrink-0">
                          {codeGenerationResult.success ? (
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                              <span className="text-green-600 text-sm">✓</span>
                            </div>
                          ) : (
                            <AlertTriangle className="h-5 w-5 text-red-400" />
                          )}
                        </div>
                        <div className="ml-3">
                          <p className={`text-sm font-medium ${
                            codeGenerationResult.success ? 'text-green-800' : 'text-red-800'
                          }`}>
                            {codeGenerationResult.message}
                          </p>
                          {codeGenerationResult.success && (
                            <div className="mt-2 text-xs text-green-700">
                              <p>Total de clientes: {codeGenerationResult.totalCustomers}</p>
                              <p>Códigos gerados: {codeGenerationResult.updatedCount}</p>
                              <p>Já possuíam código: {codeGenerationResult.alreadyHadCodes}</p>
                              {codeGenerationResult.errors && (
                                <details className="mt-2">
                                  <summary className="cursor-pointer font-medium">
                                    Erros ({codeGenerationResult.errors.length})
                                  </summary>
                                  <ul className="mt-1 ml-4 space-y-1">
                                    {codeGenerationResult.errors.map((error, index) => (
                                      <li key={index} className="text-red-600">• {error}</li>
                                    ))}
                                  </ul>
                                </details>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Visual Customization */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Palette className="mr-2 h-5 w-5 text-blue-600" />
            Personalização Visual
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cor Primária
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={settings.primaryColor}
                  onChange={(e) => handleChange('primaryColor', e.target.value)}
                  className="h-10 w-20 border border-gray-300 rounded-md cursor-pointer"
                />
                <input
                  type="text"
                  value={settings.primaryColor}
                  onChange={(e) => handleChange('primaryColor', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cor de Destaque
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={settings.accentColor}
                  onChange={(e) => handleChange('accentColor', e.target.value)}
                  className="h-10 w-20 border border-gray-300 rounded-md cursor-pointer"
                />
                <input
                  type="text"
                  value={settings.accentColor}
                  onChange={(e) => handleChange('accentColor', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Prévia</h3>
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-lg flex items-center justify-center border border-gray-200 bg-white overflow-hidden">
                {(logoPreview || settings.logo) ? (
                  <img 
                    src={logoPreview || settings.logo} 
                    alt="Logo" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div 
                    className="w-full h-full flex items-center justify-center text-white font-bold text-xs"
                    style={{ backgroundColor: settings.primaryColor }}
                  >
                    Logo
                  </div>
                )}
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900">{settings.companyName || 'Nome da Empresa'}</div>
                <div 
                  className="text-sm font-medium px-2 py-1 rounded"
                  style={{ backgroundColor: settings.accentColor, color: 'white' }}
                >
                  {settings.pointsPerReal} pts/R$
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Bell className="mr-2 h-5 w-5 text-blue-600" />
            Notificações
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                id="email-notifications"
                type="checkbox"
                checked={settings.emailNotifications}
                onChange={(e) => handleChange('emailNotifications', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="email-notifications" className="ml-2 block text-sm text-gray-700">
                Notificações por email
              </label>
            </div>

            {/* SMS apenas para admin */}
            {user?.role === 'admin' && (
              <div className="flex items-center">
                <input
                  id="sms-notifications"
                  type="checkbox"
                  checked={settings.smsNotifications}
                  onChange={(e) => handleChange('smsNotifications', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div className="ml-2">
                  <label htmlFor="sms-notifications" className="block text-sm text-gray-700">
                    Notificações por SMS
                  </label>
                  <p className="text-xs text-gray-500">
                    Requer telefone cadastrado dos clientes
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center">
              <input
                id="promotional-emails"
                type="checkbox"
                checked={settings.promotionalEmails}
                onChange={(e) => handleChange('promotionalEmails', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="promotional-emails" className="ml-2 block text-sm text-gray-700">
                Emails promocionais
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSave}
          disabled={loading || uploading}
          className={`px-6 py-2 rounded-md text-white font-medium flex items-center ${
            loading || uploading
              ? 'bg-gray-400 cursor-not-allowed' 
              : saved 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-blue-600 hover:bg-blue-700'
          } transition-colors`}
        >
          <Save className="mr-2 h-4 w-4" />
          {uploading ? 'Enviando logo...' : loading ? 'Salvando...' : saved ? 'Salvo!' : 'Salvar Configurações'}
        </button>
      </div>
    </div>
  );
};

export default CompanySettings;
